import { KeyboardEvent, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FilterChip } from "@interstate/components/Chip";
import {
  ErrorMessages,
  isValidEmail,
  sanitizeEmailInput
} from "@coxauto-ui/cxm/communications/util";

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-left: 48px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-weight: 400;
  color: #666;
  font-size: 14px;
  padding-top: 12px;
  min-width: 40px;
  flex-shrink: 0;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #959595;
  border-radius: 4px;
  min-height: 44px;
  background-color: #fff;
  flex: 1;

  &:focus-within {
    border-color: #0073e6;
    outline: 2px solid #0073e640;
  }
`;

const InlineChipInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 14px;
  padding: 4px;
`;

export interface EmailChipsInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  label: string;
  maxRecipients?: number;
  disabled?: boolean;
  testId?: string;
  onValidationChange?: (hasError: boolean) => void;
}

export function EmailChipsInput({
  value,
  onChange,
  placeholder = "Enter email address",
  label,
  maxRecipients = 20,
  disabled = false,
  testId = "email-chips-input",
  onValidationChange
}: EmailChipsInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter out empty strings from display
  const validEmails = useMemo(
    () => value.filter(email => email.trim() !== ""),
    [value]
  );

  const isAtMaxCapacity = validEmails.length >= maxRecipients;

  const normalizedEmails = useMemo(
    () => validEmails.map(email => sanitizeEmailInput(email)),
    [validEmails]
  );

  const addEmail = (email: string) => {
    const sanitized = sanitizeEmailInput(email);

    // Clear any previous errors
    setErrorMessage(null);
    onValidationChange?.(false);

    // Show error for invalid emails
    if (!sanitized || !isValidEmail(sanitized)) {
      if (email.trim()) {
        setErrorMessage(ErrorMessages.INVALID_EMAIL_ADDRESS_ERROR);
        onValidationChange?.(true);
      }
      return;
    }

    // Show error for duplicates (case-insensitive)
    if (normalizedEmails.includes(sanitized)) {
      setErrorMessage(ErrorMessages.DUPLICATE_EMAIL_ADDRESS_ERROR);
      onValidationChange?.(true);
      return;
    }

    // Enforce max recipients (shouldn't happen if input is disabled, but safety check)
    if (validEmails.length >= maxRecipients) {
      setInputValue("");
      return;
    }

    onChange([...value, sanitized]);
    setInputValue("");
  };

  const removeEmail = (index: number) => {
    // Remove from the filtered validEmails, then update the full array
    const emailToRemove = validEmails[index];
    const newEmails = value.filter(email => email !== emailToRemove);
    onChange(newEmails);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;

    // Create chip on Enter, Comma, Space, or Tab
    if (key === "Enter" || key === "," || key === " " || key === "Tab") {
      event.preventDefault();
      if (inputValue.trim()) {
        addEmail(inputValue.trim());
      }
      return;
    }

    // Backspace on empty input removes last chip
    if (key === "Backspace" && !inputValue && validEmails.length > 0) {
      removeEmail(validEmails.length - 1);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <FieldContainer>
      <FieldRow>
        <FieldLabel htmlFor={`${testId}-input`}>{label}:</FieldLabel>
        <ChipsContainer
          aria-label={label}
          data-testid={testId}
          role="group"
          onClick={handleContainerClick}
        >
          {validEmails.map((email, index) => (
            <FilterChip
              data-testid={`${testId}-chip-${index}`}
              disabled={disabled}
              id={`${testId}-chip-${index}`}
              key={email}
              label={email}
              onClick={() => !disabled && removeEmail(index)}
            />
          ))}
          <InlineChipInput
            aria-label={`${label} input`}
            data-testid={`${testId}-input`}
            disabled={disabled || isAtMaxCapacity}
            id={`${testId}-input`}
            placeholder={validEmails.length === 0 ? placeholder : ""}
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value);
              setErrorMessage(null);
            }}
            onKeyDown={handleKeyDown}
          />
        </ChipsContainer>
      </FieldRow>
      {errorMessage && (
        <ErrorMessage data-testid={`${testId}-error`}>
          {errorMessage}
        </ErrorMessage>
      )}
      {isAtMaxCapacity && !errorMessage && (
        <ErrorMessage data-testid={`${testId}-error`}>
          {ErrorMessages.MAX_RECIPIENTS_ERROR.replace(
            "{maxRecipients}",
            maxRecipients.toString()
          )}
        </ErrorMessage>
      )}
    </FieldContainer>
  );
}

export default EmailChipsInput;
